/**
 * practiceConstants.ts — Shared constants for the PracticeView ecosystem.
 *
 * All magic numbers and string literals used across PracticeView.vue,
 * its composables (usePractice*.ts), and its child components (Practice*.vue)
 * are consolidated here for extreme DRY.
 */
import type { AliyahType, HaftarahType, SentenceGroupType, SentenceType } from 'types/types'

// {{{ kippah reminder
/** Duration of inactivity (ms) before a session is considered "new" and the kippah reminder fires. */
export const SESSION_INACTIVITY_THRESHOLD_MS = 2 * 60 * 60 * 1000 // 2 hours

/** localStorage key for the last-activity timestamp used by the kippah reminder. */
export const LAST_ACTIVITY_KEY = 'lastActivityTimestamp'

/** Snackbar timeout (ms) for the kippah reminder. */
export const KIPPAH_REMINDER_TIMEOUT_MS = 5000

/** Inline SVG kippah icon for the reminder snackbar. Smooth shallow spherical cap — no peak. */
export const KIPPAH_SVG = [
  '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="12" viewBox="0 0 28 12" fill="currentColor">',
  '<path d="M3,11 C3,11 5,4 14,4 C23,4 25,11 25,11 Z" fill="currentColor"/>',
  '<path d="M6,10 C6,10 8,6 14,6 C20,6 22,10 22,10" fill="none" stroke="currentColor" stroke-width="0.5" opacity="0.35"/>',
  '</svg>',
].join('')
// }}}

// {{{ practice tracking
/** Interval (ms) between heartbeat mutations sent to the backend. */
export const HEARTBEAT_INTERVAL_MS = 30_000 // 30 seconds

/** Duration of inactivity (ms) before heartbeats stop (user is idle). */
export const IDLE_THRESHOLD_MS = 180_000 // 3 minutes

/** Default number of days shown in the practice history chart. */
export const CHART_DAYS_DEFAULT = 7

/** Maximum days for the chart slider (8 weeks). Must match backend WEEKLY_HISTORY_WEEKS × 7 (bmt/constants.py). */
export const CHART_DAYS_MAX = 56

/** Step value for the chart slider (1 week). */
export const CHART_DAYS_STEP = 7

/** Height (px) of the practice history bar chart container. */
export const CHART_HEIGHT_PX = 120

/** Max-width (px) of the chart days slider. */
export const CHART_SLIDER_MAX_WIDTH_PX = 200

/** Minimum width (px) for individual chart bar columns. */
export const CHART_BAR_MIN_WIDTH_PX = 32

/** Maximum width (px) for individual chart bars. */
export const CHART_BAR_MAX_WIDTH_PX = 60

/** Minimum bar height (px) for non-zero values in the chart. */
export const CHART_BAR_MIN_HEIGHT_PX = 4

/** Maximum scaled bar height (px) in the practice history chart. */
export const CHART_BAR_SCALE_HEIGHT_PX = 90

/** Throttle (ms) for user interaction events before updating lastInteraction timestamp */
export const INTERACTION_THROTTLE_MS = 1000

/** Polling interval (ms) for checking whether a recording has finished processing. */
export const RECORDING_REFETCH_INTERVAL_MS = 3000
// }}}

// {{{ keyboard
/** Key to advance to the next segment during manual segment jump mode */
export const NEXT_KEY = 'n'

/** Key to go to the previous sentence when not recording */
export const PREV_KEY = 'p'

/** Key to mark a phrase boundary while recording in Manual Mode (disableAutomaticVoiceStartStop) */
export const MANUAL_PHRASE_SPLIT_KEY = 'p'
// }}}

// {{{ popover timing
/** Duration (ms) to show the "Sing Now!" / "Read Now!" popup when recording starts */
export const SING_NOW_POPOVER_SHOW = 1500

/** Duration (ms) to show phrase-split visual feedback when VAD confirms silence. */
export const PHRASE_SPLIT_BLIP_MS = 800
// }}}

// {{{ FSM timing
/** How many times to repeat a segment after jump-back */
export const REPEATS = 3

/** Delay (ms) before auto-advancing to the next sentence at the end of the last segment */
export const LAST_WORD_AUTO_ADVANCE_DELAY = 1200

/** Delay (ms) before stopping after the last segment (when auto-advance is off) */
export const LAST_WORD_DELAY = 2000

/** Pause (ms) between words within a segment */
export const INTERWORD_PAUSE = 350

/** Pause (ms) between segments */
export const INTERSEGMENT_PAUSE = 1700
// }}}

// {{{ voice mode identifiers — values sent to/received from the backend API
/** Voice mode string for singing recordings (matches backend PracticeRecording.voice field). */
export const VOICE_MODE_SINGING = 'singing'

/** Voice mode string for reading recordings (matches backend PracticeRecording.voice field). */
export const VOICE_MODE_READING = 'reading'
// }}}

// {{{ recording mode identifiers
/** Single-sentence recording mode — records one sentence then stops. */
export const RECORDING_MODE_SINGLE = 'single'

/** Continuous recording mode — auto-advances and re-records sentence-by-sentence. */
export const RECORDING_MODE_CONTINUOUS = 'continuous'

/** Union type for recording modes. */
export type RecordingMode = typeof RECORDING_MODE_SINGLE | typeof RECORDING_MODE_CONTINUOUS
// }}}

// {{{ upload / voice events
/**
 * Pre-buffer (ms) matching the backend's STUDENT_VOICE_EXTRACT_PRE_BUFFER_MS (bmt/constants.py).
 * The backend includes this many ms of audio before each voice-start when building
 * the student-only file, so we must account for it when computing compressed time.
 * NOTE: Distinct from phrase_matching.VOICE_ONSET_PRE_BUFFER_MS (150ms) which is for VAD.
 */
export const STUDENT_VOICE_EXTRACT_PRE_BUFFER_MS = 500
// }}}

// {{{ localStorage keys
/** localStorage key for persisted practice settings (category, font size, etc.) */
export const PRACTICE_SETTINGS_STORAGE_KEY = 'bmt-practice-settings'

/** localStorage key for the serialized user object. */
export const LS_USER_KEY = 'user'

/** localStorage key for the serialized impersonator object (when impersonating another user). */
export const LS_IMPERSONATOR_KEY = 'impersonator'

/** localStorage key for the user's theme preference. */
export const LS_THEME_KEY = 'theme'

/** Available theme identifiers — must match backend VALID_THEMES (bmt/constants.py). */
export const THEME_LIGHT = 'light'
export const THEME_BEIGE = 'beige'
export const THEME_GREY = 'grey'
export const THEME_MIDNIGHT = 'midnight'

/** All valid theme names in display order. */
export const VALID_THEMES = [THEME_LIGHT, THEME_BEIGE, THEME_GREY, THEME_MIDNIGHT] as const

/** Type for valid theme names. */
export type ThemeName = (typeof VALID_THEMES)[number]

/** Human-readable labels for the theme selector UI. */
export const THEME_LABELS: Record<ThemeName, string> = {
  [THEME_BEIGE]: 'Warm Beige',
  [THEME_GREY]: 'Cool Grey',
  [THEME_LIGHT]: 'Light',
  [THEME_MIDNIGHT]: 'Midnight',
}
// }}}

// {{{ VAD sensitivity range and defaults
export const VOICE_SENSITIVITY_MIN = 1
export const VOICE_SENSITIVITY_MAX = 100
/** Default voice sensitivity threshold (VOICE_SENSITIVITY_MIN..VOICE_SENSITIVITY_MAX). Applied on every recording start. */
export const VOICE_SENSITIVITY_DEFAULT = 85
export const SILENCE_PATIENCE_MIN = 1
export const SILENCE_PATIENCE_MAX = 5
/** Default silence patience (SILENCE_PATIENCE_MIN..SILENCE_PATIENCE_MAX). */
export const DEFAULT_SILENCE_PATIENCE = 3
// }}}

// {{{ segmentation modes
export const RESET_TO_SENTENCE = 0
export const RESET_TO_WORDS = 1
/** Sentences with this many words or fewer are always phrased as a single phrase */
export const MAX_WORDS_STILL_PHRASED = 4
// }}}

// {{{ slider steps
export const FONT_SIZE_STEP = 10
export const PITCH_SHIFT_STEP = 0.1
// }}}

// {{{ pitch shift bounds + conversion
export const MIN_PITCH = 0.6 // don't go lower than parselmouth
export const UI_MIN_PITCH_SHIFT = 1 // multiplied by -1 in the UI
export const UI_MAX_PITCH_SHIFT = 2

/**
 * Convert UI slider value (-UI_MIN_PITCH_SHIFT..+UI_MAX_PITCH_SHIFT) to actual pitch shift string.
 * Negative values map linearly from 1.0 down to MIN_PITCH.
 * Positive values map linearly from 1.0 up to 2.0.
 */
export function uiPitchToActual(uiValue: number): string {
  return (
    uiValue < 0 ? 1.0 - Math.abs(uiValue / UI_MIN_PITCH_SHIFT) * (1.0 - MIN_PITCH) : (uiValue + UI_MAX_PITCH_SHIFT) / UI_MAX_PITCH_SHIFT
  ).toFixed(1)
}
// }}}

// {{{ AnnotatedText name identifiers
/**
 * The `name` prop passed to AnnotatedText in the practice overlay.
 * Also used as the prefix for segment DOM IDs in popoverTarget and follower callbacks.
 */
export const PRACTICE_TEXT_NAME = 'practiceText'

/** The `name` prop passed to AnnotatedText in the all-text (non-focus) view. */
export const ALL_TEXT_NAME = 'allText'

/** The `name` prop passed to AnnotatedText in the recording focus (SentenceFocus) view. */
export const FOCUS_TEXT_NAME = 'focusText'

/** The `name` prop passed to AnnotatedText in the recording playback overlay. */
export const PLAYBACK_OVERLAY_TEXT_NAME = 'playbackOverlay'

/** The `name` prop passed to AnnotatedText in the Quick Practice (listen) view. */
export const LISTEN_TEXT_NAME = 'listenText'

/** The `name` prop passed to AnnotatedText in the full-text recordings overview. */
export const OVERVIEW_TEXT_NAME = 'recordingsOverview'

/** The `name` prop passed to AnnotatedText in the custom text editor (CustomText.vue). */
export const CUSTOM_TEXT_NAME = 'newText'
// }}}

// {{{ ID extraction helpers
/**
 * Extracts integer IDs from the current content selection.
 * Used by pushFocusPoint() and upload() to avoid duplicating parseInt() calls.
 */
export function extractContentIds(
  aliyah: AliyahType | null,
  haftarah: HaftarahType | null,
  sentence: SentenceType | null,
  sentenceGroup: SentenceGroupType | null,
): {
  aliyahId: number | null
  haftarahId: number | null
  sentenceId: number | null
  sentenceGroupId: number | null
} {
  return {
    aliyahId: aliyah ? parseInt(aliyah.id) : null,
    haftarahId: haftarah ? parseInt(haftarah.reading.id) : null,
    sentenceGroupId: sentenceGroup ? parseInt(sentenceGroup.reading.id) : null,
    sentenceId: sentence ? parseInt(sentence.id) : null,
  }
}
// }}}

// {{{ segment DOM ID builder
/** Builds the DOM ID for a segment element within a named AnnotatedText instance. */
export function textSegmentId(name: string, sentenceIndex: number, segmentIndex: number): string {
  return `${name}:${sentenceIndex}:${segmentIndex}:segment`
}

export const practiceTextSegmentId = (sentenceIndex: number, segmentIndex: number) =>
  textSegmentId(PRACTICE_TEXT_NAME, sentenceIndex, segmentIndex)

export const focusTextSegmentId = (sentenceIndex: number, segmentIndex: number) =>
  textSegmentId(FOCUS_TEXT_NAME, sentenceIndex, segmentIndex)

export const listenTextSegmentId = (sentenceIndex: number, segmentIndex: number) =>
  textSegmentId(LISTEN_TEXT_NAME, sentenceIndex, segmentIndex)
// }}}

// {{{ column justification — DOM classes and selectors
/** CSS class toggled on the container during measurement (disables justify). */
export const COLUMN_MEASURING_CLASS = 'column-measuring'

/** CSS class for inline filler elements that prevent justify from over-spreading short lines. */
export const COLUMN_LINE_FILLER_CLASS = 'column-line-filler'

/** CSS class for inline elements that absorb slack at setuma/petucha section gaps. */
export const STRETCH_POINT_CLASS = 'stretch-point'

/** DOM ID suffix for word elements in AnnotatedText (e.g., "practiceText:0:1:2:word"). */
export const WORD_ELEMENT_ID_SUFFIX = ':word'

/** DOM ID suffix for boundary elements in AnnotatedText (e.g., "practiceText:0:1:2:boundary"). */
export const BOUNDARY_ELEMENT_ID_SUFFIX = ':boundary'

/** DOM ID suffix for sentence elements in AnnotatedText (e.g., "practiceText:0:sentence"). */
export const SENTENCE_ELEMENT_ID_SUFFIX = ':sentence'

/** DOM ID suffix for line-number elements in AnnotatedText (e.g., "practiceText:0:number"). */
export const NUMBER_ELEMENT_ID_SUFFIX = ':number'

/**
 * Base width (em) for column mode container at 100% font size.
 * ~42 chars/line x ~0.6em/char ≈ 25em, plus 25% margin → 32em.
 */
export const COLUMN_BASE_WIDTH_EM = 32
// }}}

// {{{ column justification — numeric thresholds
/**
 * If a line's natural width is less than this fraction of the container width,
 * a filler element is expanded to prevent CSS justify from over-spreading the words.
 */
export const COLUMN_SHORT_LINE_THRESHOLD = 0.4

/** Pixel margin subtracted from the filler width to avoid pixel-rounding overflow. */
export const COLUMN_FILLER_MARGIN_PX = 10

/**
 * Fraction of container width below which the column container is constrained to the widest
 * natural line width (prevents justify from spreading words across the full parent width). Set
 * high so that even near-full-width columns hug their text instead of spreading.
 */
export const COLUMN_WIDTH_THRESHOLD = 0.97

/**
 * Line-height for column mode — tighter than the default (DEFAULT_LINE_HEIGHT) so column lines
 * sit closer together, matching the dense spacing of a physical Torah column. Tunable.
 */
export const COLUMN_LINE_HEIGHT = 1.3

/**
 * Pixel tolerance for grouping inline elements onto the same visual line.
 * Elements whose vertical positions differ by at most this value are on the same line.
 */
export const COLUMN_LINE_GROUPING_TOLERANCE_PX = 4

/**
 * Pixel padding added to the constrained column width to avoid sub-pixel wrapping.
 */
export const COLUMN_CONSTRAIN_PADDING_PX = 2
// }}}

// {{{ UI timing
/** Default open delay (ms) for Vuetify tooltips throughout the app. */
export const TOOLTIP_OPEN_DELAY_MS = 500

/** Default timeout (ms) for snackbar notifications when no explicit timeout is provided. */
export const SNACKBAR_DEFAULT_TIMEOUT_MS = 3000
// }}}

// {{{ dialog defaults
/** Default width (px) for the ConfirmDialog component. */
export const DIALOG_DEFAULT_WIDTH = 550

/** Overlay opacity for modal dialogs (ConfirmDialog, SamplingOverlay). */
export const DIALOG_OVERLAY_OPACITY = '0.60'

/** Width of the sentence-focus recording dialog (RecordingsView). */
export const FOCUS_DIALOG_WIDTH = '90vw'

/** Scrim opacity for the sentence-focus recording dialog (RecordingsView). Darker than DIALOG_OVERLAY_OPACITY to aid focus. */
export const FOCUS_DIALOG_SCRIM_OPACITY = 0.85
// }}}

// {{{ recording spinner
/** Default size (px) for the RecordingSpinner component when no size prop is provided. */
export const RECORDING_SPINNER_DEFAULT_SIZE_PX = 44

/** Scale factor for the microphone icon inside the RecordingSpinner relative to its size. */
export const RECORDING_SPINNER_ICON_SCALE = 0.55
// }}}

// {{{ form fields
/** Minimum width for year input fields (shared by CustomAliyah and CustomHaftarah). */
export const YEAR_FIELD_MIN_WIDTH = '5.5em'
// }}}

// {{{ overlay slider dimensions
/** Min-width (px) for slider containers in the PlaybackOverlay (font size and context sliders). */
export const OVERLAY_SLIDER_MIN_WIDTH_PX = 200

/** Max-width (px) for slider containers in the PlaybackOverlay (font size and context sliders). */
export const OVERLAY_SLIDER_MAX_WIDTH_PX = 300
// }}}

// {{{ app bar styling
/** Height (px) of the Vuetify app bar. Used to offset sticky toolbars so they sit flush below it. */
export const APP_BAR_HEIGHT_PX = 62

/** Icon color for app bar buttons on dark backgrounds. */
export const APP_BAR_ICON_COLOR = '#fff'

/** Background color for the user avatar button in the app bar (non-impersonating). */
export const APP_BAR_AVATAR_BG = 'rgba(255, 255, 255, 0.2)'
// }}}

// {{{ user-facing messages
/** Dialog title when saving a recording fails. */
export const UPLOAD_FAILED_TITLE = 'Upload failed'
// }}}
